import { useDispatch, useSelector } from "react-redux";
import { useRef, useEffect, useMemo } from "react";
import TimelineList from "cxfcomponents/Timeline/TimelineList";
import CircularProgress from "@mui/material/CircularProgress";
import ReportItem from "./ReportItem";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import ExecutionReportFooter from "../ExecutionReportFooter";
import { getLocalDateTimeString } from "helpers/common";
import { Virtuoso } from "react-virtuoso";
import { addReportState } from "redux/actions/flowDesigner/flowExecutionActionCreator";

export default function ReportsTimeline() {
  const currentSavedFlow = useSelector((store) => store.designer.flows.currentSavedFlow);
  const { currentExecutionReportSessionId, flowReports, selectedLogNodeId } = useSelector(
    (store) => store?.designer?.flowExecution
  );
  const { isExecuting } = useSelector((store) => store?.designer?.tabs?.currentFlowTab);
  const virtuosoRef = useRef(null);
  const dispatch = useDispatch();

  const flowReport = useMemo(() => {
    return flowReports[currentSavedFlow?.id]?.reports;
  }, [currentSavedFlow?.id, flowReports]);

  const reportState = useMemo(() => {
    return flowReports[currentSavedFlow?.id]?.state;
  }, [currentSavedFlow?.id, flowReports]);

  const renderReports = (index) => {
    if (!flowReport) return null;

    const { type, payload: report } = flowReport[index];

    switch (type) {
      case "reportSession":
        return (
          <SoftBox key={`reportSession-${report.sessionId}`}>
            <SoftBox>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                Session ID: {report.sessionId}
              </SoftTypography>
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                Date & Time: {getLocalDateTimeString(report.time)}
              </SoftTypography>
            </SoftBox>
            <SoftTypography variant="button" fontWeight="regular" color="text">
              Flow Name: {report.name}
            </SoftTypography>
          </SoftBox>
        );
      case "reportData":
        return (
          <ReportItem
            key={`reportData-${report.sessionId}-${report.nodeId}-${report.duration}`}
            item={report}
            isFirst={report.isFirst}
            isLast={flowReport ? flowReport[index + 1]?.type === "sessionEnd" : false}
            isLastReportData={report?.sessionId === currentExecutionReportSessionId}
          />
        );
      case "sessionEnd":
        return (
          <ExecutionReportFooter
            key={`sessionEnd-${report.sessionId}`}
            totalDuration={report.totalDuration}
            totalNode={report.totalNodes}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (selectedLogNodeId && flowReport?.length > 0 && virtuosoRef.current) {
      const selectedIndex = flowReport.findLastIndex(
        (item) => item.type === "reportData" && item.payload?.nodeId === selectedLogNodeId
      );

      if (selectedIndex !== -1) {
        setTimeout(() => {
          virtuosoRef.current.scrollToIndex({
            index: selectedIndex,
            align: "center",
            behavior: "smooth"
          });
        }, 50);
      }
    }
  }, [selectedLogNodeId, flowReport]);

  useEffect(() => {
    if (flowReport?.length > 0 && virtuosoRef.current)
        virtuosoRef.current.scrollToIndex({ index: flowReport ? flowReport.length : 0 });
  }, [flowReport, isExecuting]);

  useEffect(() => {
    let localRef = null;

    if (virtuosoRef.current) localRef = virtuosoRef.current;

    return () => {
      localRef.getState((state) => {
        dispatch(addReportState({ flowId: currentSavedFlow.id, state }));
      });
    };
  }, []);

  return (
    <TimelineList className="reports-timeline" title="">
      <div style={{ height: "100%", position: "relative" }}>
        <Virtuoso
          ref={virtuosoRef}
          style={{ height: "100%", width: "100%" }}
          totalCount={flowReport ? flowReport.length : 0}
          itemContent={(index) => {
            return renderReports(index);
          }}
          increaseViewportBy={{ top: 800, bottom: 300 }}
          restoreStateFrom={
            reportState
              ? {
                  ...reportState,
                  scrollTop: 999999
                }
              : undefined
          }
        />

        {isExecuting && (
          <SoftBox
            sx={{
              display: "flex",
              justifyContent: "center",
              position: "absolute",
              bottom: "24px",
              left: "50%",
              transform: "translateX(-50%)",
              overflow: "hidden"
            }}
          >
            <CircularProgress size={24} />
          </SoftBox>
        )}
      </div>
    </TimelineList>
  );
}
